import {
    type RefObject,
    useContext,
    useEffect,
    useLayoutEffect,
} from "react";
import {
    PageTimelineContext,
    type PositionContextType,
} from "../../displayComponents/displayElements/DisplayElements";

import { strings } from "@quickpoint.me/shared";
import gsap from "gsap";
import { useRecoilValue } from "recoil";
import { NavigationType } from "../../state/navigation";
import {
    IsInPlayWrightTest,
    PageElementsFamily,
    PageIndex,
    createLocation,
} from "../../state/page";
import {
    IsInAuthor,
    IsPreviewGenerator,
    TapDirection,
} from "../../state/ui";
import { StickyContext } from "../../displayComponents/displayElements/Sticky";

export interface BackgroundAnimationEffectProps {
    target: RefObject<HTMLDivElement> | string;
    pageIndex: number;
    position: PositionContextType;
    atomId: number;
}

function useBackgroundAnimation({
    target,
    pageIndex,
    position,
    atomId,
}: BackgroundAnimationEffectProps) {
    const left = position === "left";
    const right = position === "right";
    const isInSticky = useContext(StickyContext);
    const pageTimeline = useContext(PageTimelineContext);

    // Recoil states
    const direction = useRecoilValue(TapDirection);
    const isInAuthor = useRecoilValue(IsInAuthor);
    const isPreviewer = useRecoilValue(IsPreviewGenerator);
    const isInPlayWrightTest = useRecoilValue(IsInPlayWrightTest);
    const navigationType = useRecoilValue(NavigationType);
    const globalPageIndex = useRecoilValue(PageIndex);
    const key = createLocation(pageIndex, atomId);
    const elementData = useRecoilValue(PageElementsFamily(key))?.data;
    const modifierOpacity = elementData?.modifier?.["opacity:"]
        ? elementData.modifier["opacity:"]
        : "1";

    //tests
    useEffect(() => {
        const animationTarget = typeof target === "string" ? target : target.current;
        if ((isInPlayWrightTest || isInAuthor) && animationTarget)
            gsap.set(animationTarget, { autoAlpha: 1 });
    });

    //tap animations
    useLayoutEffect(() => {
        if (navigationType !== "tap") return;
        if (isInSticky) return;
        if (isPreviewer || isInPlayWrightTest) return;
        let ctx: gsap.Context;

        const timeline = gsap.timeline();
        const animationTarget = typeof target === "string" ? target : target.current;

        if (animationTarget) {
            ctx = gsap.context(() => {
                if (direction === "init") {
                    /* on init the CURRENT page sits in the right container
                       whenever the deck was deep-linked past page 1
                       (RightPageIndex === PageIndex), so left/right says
                       nothing about visibility — show the container that
                       holds the current page's background, hide the other */
                    const isCurrentPage = pageIndex === globalPageIndex;
                    gsap.set(animationTarget, {
                        autoAlpha: isCurrentPage
                            ? modifierOpacity
                                ? modifierOpacity
                                : 1
                            : 0,
                    });
                }

                if (direction === "back") {
                    if (right) {
                        timeline.set(animationTarget, { autoAlpha: 1 });
                        timeline.to(animationTarget, { autoAlpha: 0 });
                    }

                    if (left) {
                        timeline.set(animationTarget, { autoAlpha: 0 });
                        timeline.to(animationTarget, {
                            autoAlpha: modifierOpacity ? modifierOpacity : 1,
                        });
                    }
                }

                if (direction === "forward") {
                    if (left) {
                        timeline.set(animationTarget, { autoAlpha: 0 });
                    }

                    if (right) {
                        timeline.set(animationTarget, { autoAlpha: 0 });
                        timeline.to(animationTarget, {
                            autoAlpha: modifierOpacity ? modifierOpacity : 1,
                        });
                    }
                }
            });

            if (isInPlayWrightTest) gsap.set(animationTarget, { autoAlpha: 1 });
        }

        return () => {
            if (ctx) {
                ctx.revert();
            }
        };
    }, [
        direction,
        target,
        isPreviewer,
        isInPlayWrightTest,
        navigationType,
        isInSticky,
        modifierOpacity,
        left,
        right,
        pageIndex,
        globalPageIndex,
    ]);

    useLayoutEffect(() => {
        const topId = strings.navigation.idStoryBackgroundTop;
        const bottomId = strings.navigation.idStoryBackgroundBottom;

        if (isInSticky || isPreviewer || isInPlayWrightTest || isInAuthor) return;
        if (
            navigationType !== "scroll" &&
            navigationType !== "scrollCards" &&
            navigationType !== "scrollContinuous"
        )
            return;

        // This animation is controlled only by the 'top' background element to avoid conflicts.
        if (position !== "top") return;
        if (!pageTimeline) return;

        const ctx = gsap.context(() => {
            // Kill any existing tweens on both background elements
            gsap.killTweensOf([topId, bottomId]);

            // Set initial states for the cross-fade. Top is visible, bottom is hidden.
            gsap.set(topId, { autoAlpha: 1 });
            gsap.set(bottomId, { autoAlpha: 0 });

            // Create the cross-fade animation starting at 70% of the scroll.
            // It will complete at 99% (0.7 + 0.29).
            pageTimeline.to(topId, { autoAlpha: 0, duration: 0.49 }, 0.5);
            pageTimeline.to(bottomId, { autoAlpha: 1, duration: 0.49 }, 0.5);
        });

        return () => ctx.revert();
    }, [
        pageTimeline,
        position,
        isInSticky,
        isPreviewer,
        isInPlayWrightTest,
        isInAuthor,
        navigationType,
    ]);
}

export default useBackgroundAnimation;